<script lang="ts">
  import type { PaginationInfo } from '../types/paginate';
  import type {
    CategoryConfig,
    CategoryPropConfig,
  } from '../modules/projects/project.interface';
  import { createEventDispatcher } from 'svelte';
  import { Button, Icon } from 'svelte-materialify';
  import Select from './Select.svelte';
  import type { ItemStatus } from '../modules/item/item.constants';
  import { location, push, querystring } from 'svelte-spa-router';
  import type { ListQuery } from '../types/listQuery';
  import { day, sorting } from '../store';

  export let paginationInfo: PaginationInfo;
  export let categoryConfig: CategoryConfig;
  export let sort: string;

  $: query = getQuery($querystring);
  interface TimeOptions {
    timeOption: string;
    days: number;
  }

  const timeOptions: Array<TimeOptions> = [
    { timeOption: '24h', days: 1 },
    { timeOption: '7 Days', days: 7 },
    { timeOption: 'Last 30 Days', days: 30 },
    { timeOption: 'All Times', days: null },
  ];

  const dispatch = createEventDispatcher();

  $: allPages = generatePages(paginationInfo);

  function generatePages(paginationInfo: PaginationInfo) {
    if (!paginationInfo) return [];

    return Array.from({ length: paginationInfo.totalPages }, (_, i) => i + 1);
  }

  function paginate(page: number) {
    dispatch('page-change', page);
  }

  function navigateDay(day) {
    navigate(query.status, query.source, day);
  }

  function getSortings() {
    return categoryConfig.candidateSortings.map((sortingKey) => {
      if (sortingKey === 'createdAt') {
        return {
          name: 'Date',
          value: sortingKey,
        };
      }

      let prop: CategoryPropConfig;

      if (sortingKey.includes('details.')) {
        prop = categoryConfig.details.find(
          (p) => p.key === sortingKey.replace('details.', '')
        );
      } else {
        prop = categoryConfig.props.find((p) => p.key === sortingKey);
      }

      return {
        name: prop.name,
        value: sortingKey,
      };
    });
  }

  function onSortingSelect(sortingName: string) {
    dispatch('sorting-change', sortingName);
    if (sortingName === 'details.publishedAt') {
      sorting.setSelectedSorting({ name: 'Published At', value: 'details.publishedAt' });
    } else {
      sorting.setSelectedSorting({ name: 'Date', value: 'createdAt' });
    }
  }

  function getQuery($queryString: string): Omit<ListQuery, 'category'> {
    const queryParams = new URLSearchParams($queryString);

    const query: Omit<ListQuery, 'category'> = {
      source: queryParams.get('source'),
      status: queryParams.get('status') as ItemStatus,
    };
    return query;
  }

  function navigate(status: ItemStatus, source?: string, days?: number) {
    let path = `${$location}?status=${status}`;
    if (source) {
      path += `&source=${source}`;
    }
    if (days) {
      path += `&days=${days}`;
    }
    push(path);
  }
</script>

{#if paginationInfo}
  <div class="pagination">
    <span class="pagination-counts"
      >{paginationInfo.pagingCounter} -
      {Math.min(paginationInfo.page * paginationInfo.limit, paginationInfo.totalDocs)}
      of {paginationInfo.totalDocs}</span
    >
    <div class="pagination-actions">
      <Button
        icon
        on:click={() => paginate(paginationInfo.prevPage)}
        disabled={!paginationInfo.hasPrevPage}
      >
        <Icon class="mdi mdi-chevron-left" />
      </Button>
      <Select
        className="page-select"
        items={allPages}
        selectedItem={paginationInfo.page}
        on:select={(evt) => paginate(evt.detail)}
      />
      <Button
        icon
        on:click={() => paginate(paginationInfo.nextPage)}
        disabled={!paginationInfo.hasNextPage}
      >
        <Icon class="mdi mdi-chevron-right" />
      </Button>
    </div>
    <div class="pagination-sorting">
      <div class="day-select-container">
        {#each timeOptions as timeOption}
          <button
            class="day-select"
            class:active={$day === timeOption.days}
            on:click={() => {
              day.setSelectedDay(timeOption.days);
              navigateDay($day);
            }}>{timeOption.timeOption}</button
          >
        {/each}
      </div>
      {#if categoryConfig.candidateSortings?.length}
        <div>
          <Select
            className="sorting-select"
            items={getSortings()}
            selectedItem={sort}
            displayKey="name"
            bindKey="value"
            on:select={(evt) => onSortingSelect(evt.detail)}
          />
        </div>
      {/if}
    </div>
  </div>
{/if}

<style lang="scss">
  .pagination {
    display: flex;
    align-items: center;
    padding: var(--gap) calc(var(--gap) * 2);
    border-top: 1px solid var(--theme-dividers);
  }

  .pagination-counts {
    display: inline-block;
    width: 140px;
  }

  .pagination-actions {
    display: inline-flex;
  }

  .pagination-sorting {
    display: inline-flex;
    flex: 1;
    justify-content: flex-end;
    .day-select-container {
      margin-right: 5px;
      .day-select {
        margin: 5px;
        background-color: #dcdcdc;
        border-radius: 5px;
        padding: 2px 8px;
        &.active {
          color: #dcdcdc;
          background-color: #5c5c5c;
        }
      }
    }
  }

  :global(.pagination .s-menu) {
    top: auto !important;
    bottom: 0 !important;
  }

  :global(.pagination .page-select) {
    width: 70px;
    display: flex;
    align-items: center;
    border-radius: 3px;
  }

  :global(.pagination .page-select:hover) {
    background-color: var(--theme-text-fields-filled);
  }

  :global(.pagination-actions .s-text-field__wrapper:before) {
    display: none;
  }

  :global(.pagination-actions .s-text-field__wrapper [slot='append']) {
    display: none;
  }

  :global(.pagination-actions .s-input__slot) {
    margin: 0;
  }

  :global(.pagination-actions .s-text-field__input input) {
    text-align: center;
  }
</style>
